<script lang="ts" setup>
import scrollLock from 'scroll-lock'

const { gtag } = useGtag()
const gtm = useGtm()
const config = useRuntimeConfig()

const isOpen = ref(false)

interface ISettings {
  marketing: boolean
  preferences: boolean
}

const settings = ref<ISettings>({
  marketing: true,
  preferences: false
})

const enableAnalytics = computed(() =>
  ['https://www.caseclub.in.th', 'https://qa.caseclub.in.th'].includes(
    config.public.siteUrl
  )
)

// Utils
const updateConsent = (type: 'default' | 'update', settings: ISettings) => {
  if (settings.marketing) {
    gtag('consent', type, { ad_user_data: 'granted' })
    if (gtm && enableAnalytics.value) gtm.enable()
  }

  if (settings.preferences) {
    gtag('consent', type, {
      ad_personalization: 'granted',
      ad_storage: 'granted',
      analytics_storage: 'granted'
    })
  }
}

const saveSettingsToLocalStorage = (settings: ISettings) => {
  localStorage.setItem('cookie-settings', JSON.stringify(settings))
}

const handleAcceptAll = () => {
  const newSettings = { marketing: true, preferences: true }
  saveSettingsToLocalStorage(newSettings)
  updateConsent('update', newSettings)

  isOpen.value = false
  scrollLock.enablePageScroll()
}

const handleSaveSettings = () => {
  saveSettingsToLocalStorage(settings.value)
  updateConsent('update', settings.value)

  isOpen.value = false
  scrollLock.enablePageScroll()
}

// Lifecycle
onMounted(() => {
  const raw = localStorage.getItem('cookie-settings')

  if (!raw) {
    isOpen.value = true
    scrollLock.disablePageScroll()
    return
  }

  try {
    const parsed: ISettings = JSON.parse(raw)
    updateConsent('default', parsed)
  } catch (e) {
    isOpen.value = true
    scrollLock.disablePageScroll()
  }
})
</script>

<template>
  <section
    v-if="isOpen"
    class="fixed inset-0 z-50 flex justify-center items-center"
    role="dialog"
    aria-modal="true"
    aria-labelledby="cookieConsentTitle"
    aria-describedby="cookieConsentDescription"
  >
    <div class="bg-white relative z-10 px-6 py-4 rounded-2xl max-w-[335px]">
      <!-- Header -->
      <header class="flex items-center mb-3">
        <div
          class="bg-gray-10 rounded-full w-9 h-9 flex items-center justify-center"
          aria-hidden="true"
        >
          <Icon name="cookie-bite" />
        </div>
        <h2 id="cookieConsentTitle" class="font-bold ml-4 text-base">
          {{ $t('cookie.title') }}
        </h2>
      </header>

      <!-- Description -->
      <p
        id="cookieConsentDescription"
        class="text-sm border-b border-border-divider pb-4 mb-4 mt-1"
      >
        {{ $t('cookie.desc') }}
        <NuxtLinkLocale
          to="/personal-data-consent"
          class="font-bold underline hover:opacity-50"
        >
          {{ $t('cookie.more') }}
        </NuxtLinkLocale>
      </p>

      <!-- Settings Options -->
      <div class="mb-4 space-y-4">
        <div>
          <div class="flex justify-between items-center">
            <strong class="text-sm">{{ $t('cookie.titlePermission1') }}</strong>
            <Switch
              :checked="settings.marketing"
              @change="(next) => (settings.marketing = next)"
              aria-label="Toggle marketing cookies"
            />
          </div>
          <p class="text-sm max-w-[235px] text-gray-700">
            {{ $t('cookie.descPermission1') }}
          </p>
        </div>
        <div>
          <div class="flex justify-between items-center">
            <strong class="text-sm">{{ $t('cookie.titlePermission2') }}</strong>
            <Switch
              :checked="settings.preferences"
              @change="(next) => (settings.preferences = next)"
              aria-label="Toggle preferences cookies"
            />
          </div>
          <p class="text-sm max-w-[235px] text-gray-700">
            {{ $t('cookie.descPermission2') }}
          </p>
        </div>
      </div>

      <!-- Actions -->
      <div class="grid grid-cols-2 gap-x-2">
        <button
          type="button"
          class="bg-gray-10 border border-border-divider text-sm font-bold py-3 text-center rounded-e-md rounded-s-2xl cursor-pointer max-md:text-[12px]"
          @click="handleAcceptAll"
        >
          {{ $t('cookie.btnAccept') }}
        </button>
        <button
          type="button"
          class="bg-black text-white border border-border-divider text-sm font-bold py-3 text-center rounded-s-md rounded-e-2xl cursor-pointer max-md:text-[12px]"
          @click="handleSaveSettings"
        >
          {{ $t('cookie.btnSet') }}
        </button>
      </div>
    </div>

    <!-- Overlay -->
    <div class="fixed inset-0 bg-black opacity-80" />
  </section>
</template>
